import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { CloudInventorySurfaceAttachmentDto } from '@terra/shared-contracts-external-network';
import { externalNetworkInventoryApi } from '../../clients/tester-client/external-network-inventory-api';
import { EXTERNAL_NETWORK_SCOPE_NETBLOCKS_KEY } from './useExternalNetworkScope';

export const EXTERNAL_NETWORK_CLOUD_INVENTORY_ATTACHMENTS_KEY =
  'external-network-cloud-inventory-attachments';

const invalidateCloudInventoryQueries = ({
  queryClient,
  surfaceId,
}: {
  queryClient: QueryClient;
  surfaceId: string;
}) => {
  void queryClient.invalidateQueries({
    queryKey: [EXTERNAL_NETWORK_CLOUD_INVENTORY_ATTACHMENTS_KEY, surfaceId],
  });
  void queryClient.invalidateQueries({
    queryKey: [EXTERNAL_NETWORK_SCOPE_NETBLOCKS_KEY, surfaceId],
  });
};

export function useExternalNetworkCloudInventoryAttachments({
  surfaceId,
  enabled = true,
}: {
  surfaceId: string;
  enabled?: boolean;
}) {
  const result = useQuery({
    queryKey: [EXTERNAL_NETWORK_CLOUD_INVENTORY_ATTACHMENTS_KEY, surfaceId],
    queryFn: async () => {
      const response =
        await externalNetworkInventoryApi.listCloudInventoryAttachments(
          surfaceId,
        );

      return response.attachments;
    },
    enabled,
  });

  return {
    attachments: result.data ?? [],
    isLoading: result.isLoading,
    isError: result.isError,
  };
}

export function useAttachExternalNetworkCloudInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: externalNetworkInventoryApi.attachCloudInventory,
    onSuccess: (_data, { surfaceId }) => {
      invalidateCloudInventoryQueries({ queryClient, surfaceId });
    },
  });
}

export function useDetachExternalNetworkCloudInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: externalNetworkInventoryApi.detachCloudInventory,
    onSuccess: (_data, { surfaceId }) => {
      invalidateCloudInventoryQueries({ queryClient, surfaceId });
    },
  });
}

export function useSyncExternalNetworkCloudInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: externalNetworkInventoryApi.syncCloudInventory,
    onSuccess: (_data, { surfaceId }) => {
      invalidateCloudInventoryQueries({ queryClient, surfaceId });
    },
  });
}

export function useUpdateExternalNetworkCloudAccountPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: externalNetworkInventoryApi.updateCloudAccountPolicy,
    onMutate: async ({ surfaceId, accountId, body }) => {
      const queryKey = [
        EXTERNAL_NETWORK_CLOUD_INVENTORY_ATTACHMENTS_KEY,
        surfaceId,
      ];

      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<CloudInventorySurfaceAttachmentDto[]>(
          queryKey,
        );

      queryClient.setQueryData<CloudInventorySurfaceAttachmentDto[]>(
        queryKey,
        (current) =>
          current?.map((attachment) =>
            attachment.accountId === accountId
              ? {
                  ...attachment,
                  enabled: body.enabled ?? attachment.enabled,
                  autoScope: body.autoScope,
                }
              : attachment,
          ),
      );

      return { previous, queryKey };
    },
    onError: (_error, _variables, context) => {
      if (!context?.previous) {
        return;
      }

      queryClient.setQueryData(context.queryKey, context.previous);
    },
    onSettled: (_data, _error, { surfaceId }) => {
      invalidateCloudInventoryQueries({ queryClient, surfaceId });
    },
  });
}
